import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, FormSelect } from 'components/common';
import { ISelectOption } from 'components/common/Select/types';
import { ApplicationFormPageEnum } from '../types';
import { relationshipQuestions } from './questions';
import { IRelationshipForm } from './types';
import { useNavigateBack } from 'hooks';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from 'api/applicationForm';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { parseApplicationInProgress } from '../helpers';
import { useEffect } from 'react';
import { getCurrentRoute } from 'utils/location';

export const Relationship = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);

  const form = useForm<IRelationshipForm>({
    defaultValues: {
      relationship: null,
    },
  });

  const formFields = form.watch();

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IRelationshipForm) => {
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: intl.formatMessage({ id: relationshipQuestions[key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.FULL_NAME);
    setSearchParams(searchParams);
  };

  const isAnyFieldWithError = formFields.relationship === null || Boolean(Object.values(form.formState.errors).length);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div>
          <FormattedMessage id={relationshipQuestions.relationship.labelKey} tagName={'h4'} />
          <FormSelect
            name={'relationship'}
            options={relationshipQuestions.relationship.options}
            className={'w-full'}
            selectProps={{
              isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                option.value === selectedOptionValue,
              value: formFields.relationship,
            }}
            selectInputProps={{
              placeholder: intl.formatMessage({ id: 'placeholders.select' }),
            }}
            rules={{ required: intl.formatMessage({ id: 'common.error.requiredField' }) }}
          />
        </div>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={isAnyFieldWithError}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
